BryanAnthonys.loadedScripts['storefront-catalog'] = true;

class JsonImage {
    constructor(imageData, isFromGraphQL = false) {
        if (isFromGraphQL) {
            this.id = StorefrontCatalog.idFromGid(imageData.id);
            this.alt = imageData.altText;
            this.src = imageData.url;
        } else {
            this.id = imageData.id;
            this.alt = imageData.alt;
            this.src = imageData.src;
        }

        this.width = imageData.width;
        this.height = imageData.height;
    }

    getAlt(defaultValue = '') {
        return this.alt ? this.alt : BryanAnthonys.escapeHtml(defaultValue);
    }

    getHeightRatio() {
        return this.width && this.height ? this.height / this.width : 1;
    }
}

class JsonVariant {
    constructor(variantData, isFromGraphQL = false) {
        this.variant = null;

        if (isFromGraphQL) {
            this.initFromGraphQl(variantData);
        } else {
            this.variant = variantData;
        }
    }

    initFromGraphQl(variantData) {
        this.variant = {
            id: StorefrontCatalog.idFromGid(variantData.id),
            title: variantData.title,
            price: BryanAnthonys.dollarsToCents(variantData.price.amount),
            compare_at_price: variantData.compareAtPrice
                ? BryanAnthonys.dollarsToCents(variantData.compareAtPrice.amount)
                : null
        };

        if (Object.prototype.hasOwnProperty.call(variantData, 'availableForSale')) {
            this.variant.available = variantData.availableForSale;
        }

        if (Object.prototype.hasOwnProperty.call(variantData, 'currentlyNotInStock')) {
            this.variant.currently_not_in_stock = variantData.currentlyNotInStock;
        }

        if (variantData.image) {
            this.variant.image_id = StorefrontCatalog.idFromGid(variantData.image.id);
        }

        if (Object.prototype.hasOwnProperty.call(variantData, 'quantityAvailable')) {
            this.variant.inventory_quantity = variantData.quantityAvailable;
        }

        ['option1', 'option2', 'option3'].forEach((optionKey, index) => {
            if (variantData.selectedOptions[index]) {
                this.variant[optionKey] = variantData.selectedOptions[index].value;
            } else {
                this.variant[optionKey] = null;
            }
        });
    }

    getId() {
        return this.variant.id;
    }

    getOption(index) {
        return this.variant[`option${index}`];
    }

    isAvailable() {
        if (Object.prototype.hasOwnProperty.call(this.variant, 'available')) {
            return this.variant.available;
        }

        if (Object.prototype.hasOwnProperty.call(this.variant, 'inventory_quantity')) {
            return this.variant.inventory_quantity > 0;
        }

        return true;
    }

    getInventoryQuantity() {
        if (Object.prototype.hasOwnProperty.call(this.variant, 'inventory_quantity')) {
            return this.variant.inventory_quantity;
        }

        return null;
    }

    getImage(images = []) {
        if (this.variant.featured_image) {
            return new JsonImage(this.variant.featured_image);
        }

        if (this.variant.image_id) {
            return images.find(image => image.id === this.variant.image_id);
        }

        return null;
    }

    getPrice() {
        return this.variant.price;
    }

    getCompareAtPrice() {
        if (this.variant.compare_at_price && this.variant.compare_at_price > this.variant.price) {
            return this.variant.compare_at_price;
        }

        return null;
    }

    getTitle() {
        return this.variant.title;
    }
}

// eslint-disable-next-line no-unused-vars
class JsonProduct {
    constructor(productData = null, isFromGraphQL = false) {
        this.product = null;

        if (!productData) {
            this.initFromNull();
        } else if (isFromGraphQL) {
            this.initFromGraphQl(productData);
        } else {
            this.initFromAjax(productData);
        }
    }

    initFromNull() {
        this.product = {};
        this.product.variants = [];
        this.product.images = [];
    }

    initFromGraphQl(productData) {
        this.product = {
            id: StorefrontCatalog.idFromGid(productData.id),
            title: productData.title,
            handle: productData.handle,
            created_at: productData.createdAt,
            updated_at: productData.updatedAt,
            published_at: productData.publishedAt,
            tags: productData.tags,
            body_html: productData.descriptionHtml,
            product_type: productData.productType
        };

        if (Object.prototype.hasOwnProperty.call(productData, 'availableForSale')) {
            this.product.available = productData.availableForSale;
        }

        if (Object.prototype.hasOwnProperty.call(productData, 'isGiftCard')) {
            this.product.gift_card = productData.isGiftCard;
        }

        if (Object.prototype.hasOwnProperty.call(productData, 'requiresSellingPlan')) {
            this.product.requires_selling_plan = productData.requiresSellingPlan;
        }

        if (productData.variants.edges.length > 1) {
            this.product.options = productData.options.map((option, index) => {
                option.position = index + 1;

                return option;
            });
        } else {
            this.product.options = [];
        }

        this.product.images = productData.images.edges
            .map(imageEdge => new JsonImage(imageEdge.node, true));

        this.product.variants = productData.variants.edges
            .map(variantEdge => new JsonVariant(variantEdge.node, true));
    }

    initFromAjax(productData) {
        this.product = productData;
        this.product.images = this.product.media.filter(media => media.media_type === 'image')
            .map(media => new JsonImage(media));
        this.product.variants = productData.variants && productData.variants.length > 0
            ? productData.variants.map(variant => new JsonVariant(variant))
            : [];
        if (productData.options.length && typeof productData.options[0] === 'string') {
            this.product.options = productData.options.map((option, index) => {
                const position = index + 1;
                const values = new Set();
                this.product.variants.forEach(variant => values.add(variant.getOption(position)));

                return {
                    name: option,
                    position,
                    values: Array.from(values)
                };
            });
        }
    }

    isEmpty() {
        return typeof this.product.title === 'undefined';
    }

    getId() {
        return this.product.id || '';
    }

    getHandle() {
        return this.product.handle || '';
    }

    getType() {
        return this.product.product_type || '';
    }

    getTitle(defaultValue = '') {
        const title = this.product.title || defaultValue;
        // Temporary fix to an encoding issue that appears to occur due to using
        // base 64 encode and decode on the product json
        return title.replace('â\x80\x94', '—');
    }

    getUrl() {
        if (this.product.handle) {
            return `/products/${this.product.handle}`;
        } else {
            return '/';
        }
    }

    isAvailable() {
        if (Object.prototype.hasOwnProperty.call(this.product, 'available')) {
            return this.product.available;
        }

        const availableVariant = this.product.variants.find(variant => variant.isAvailable());

        return Boolean(availableVariant);
    }

    /**
     * @returns {[JsonVariant]}
     */
    getVariants() {
        return this.product.variants;
    }

    /**
     * @returns {JsonVariant}
     */
    firstAvailableVariant() {
        return this.product.variants.find(variant => variant.isAvailable());
    }

    /**
     * @returns {JsonVariant}
     */
    firstVariant() {
        return this.product.variants[0];
    }

    hasImages() {
        return this.product.images && this.product.images.length > 0;
    }

    /**
     * @returns {[JsonImage]}
     */
    getImages() {
        return this.product.images;
    }

    /**
     * @returns {string[]}
     */
    getTags() {
        if (!this.product.tags) {
            return [];
        }

        if (Array.isArray(this.product.tags)) {
            return this.product.tags;
        }

        return this.product.tags.split(',')
            .map(tag => tag.trim());
    }

    isOnPreorder() {
        if (this.getTags().includes(BryanAnthonys.preorderTag)) {
            return true;
        }

        if (this.firstVariant().getInventoryQuantity() !== null) {
            const preorderVariant = this.getVariants().find(
                variant => variant.isAvailable() && variant.getInventoryQuantity() < 1
            );

            return Boolean(preorderVariant);
        }

        return false;
    }

    isSoftPreorder() {
        return this.getTags().includes(BryanAnthonys.softPreorderTag);
    }

    isCustomizable() {
        const tags = this.getTags().map(tag => tag.toLowerCase());

        return BryanAnthonys.customizableTypes.includes(this.getType())
            || BryanAnthonys.customizableTags.filter(tag => tags.includes(tag.toLowerCase())).length;
    }

    getDate(type) {
        if (this.product[type]) {
            return new Date(this.product[type]);
        }

        return null;
    }

    /**
     * @returns {[{name, position, values}]}
     */
    getOptions() {
        return this.product.options;
    }
}

window.StorefrontCatalog = {
    /**
     * @param product {JsonProduct}
     * @param variant {JsonVariant}
     * @param options {object}
     * @returns {string}
     */
    renderVerticalProductCard: function (product, variant = null, options = {}) {
        const element = product.isEmpty() ? 'div' : 'product-card';

        if (!options.cardClasses) {
            options.cardClasses = `card product-card card--media product-card--${BryanAnthonys.handleize(product.getType())}`;
        }

        if (!options.mediaClasses) {
            options.mediaClasses = `card__media card-media--product product-media ratio`;
        }

        if (!options.mediaRatio) {
            options.mediaRatio = 125;
        }

        if (!options.headingClasses) {
            options.headingClasses = `card__heading h4 letter-spacing-1 uppercase underline-links-hover mb-2 mt-2 size-13 size-15-desktop`;
        }

        if (!options.sectionId) {
            options.sectionId = `random-id-${parseInt(Math.random() * 1000)}-${parseInt(Math.random() * 1000)}`;
        }

        if (!options.imageWidth) {
            options.imageWidth = 700;
        }

        const selectedVariant = variant || product.firstAvailableVariant() || product.firstVariant();
        let featuredImage = '';
        if (product.hasImages()) {
            const variantImage = selectedVariant.getImage(product.getImages());
            const image = variantImage || product.getImages()[0];
            featuredImage = `
                <img
                    src="${BryanAnthonys.resizeImage(image.src, options.imageWidth)}"
                    class="motion-reduce" loading="lazy" width="${image.width}" height="${image.height}"
                    data-product-card-image alt="${image.getAlt(product.getTitle())}"
                />
            `;
        }

        let price = '', badges = '', rating = '', swatches = '';
        if (!product.isEmpty()) {
            price = StorefrontCatalog.renderPrice(product, variant);
            badges = StorefrontCatalog.renderBadges(product, variant);
            if (!options.hideRating) {
                rating = StorefrontCatalog.renderRating(product);
            }
            if (!options.hideSwatches) {
                swatches = StorefrontCatalog.renderSwatches(
                    product,
                    selectedVariant,
                    options.swatchOptions || BryanAnthonys.swatches.cardSwatchesOptions
                );
            }
        }

        return `
            <${element} class="${options.cardClasses}" data-card-product-id="${product.getId()}" data-card-variant-id="${selectedVariant ? selectedVariant.getId() : ''}">
                <div data-slide-media class="${options.mediaClasses}" style="--ratio-percent: ${options.mediaRatio}%;">
                    <a
                        class="media media--hover-effect media--transparent media--anchor size-13 size-14-desktop" href="${product.getUrl()}"
                        aria-labelledby="CardLink-${options.sectionId}-${product.getId()}"
                        data-product-card-link tabindex="-1"
                    >
                        ${featuredImage}
                    </a>
                    ${badges}
                </div>
                <div class="card__content">
                    <h3 class="${options.headingClasses}" id="title-${options.sectionId}">
                        <a
                          href="${product.getUrl()}" data-product-card-link class="full-unstyled-link size-13 size-14-desktop"
                          id="CardLink-${options.sectionId}-${product.getId()}"
                        >
                            ${product.getTitle('&nbsp;')}
                        </a>
                    </h3>
                    <div class="card-footer">
                        ${rating}
                        ${price}
                        ${swatches}
                    </div>
                </div>
            </${element}>
        `;
    },
    /**
     * @param product {JsonProduct}
     * @param variant {JsonVariant}
     * @param options {object} Use discount property between 1 and 99 to apply a discount to the sale price
     * @returns {string}
     */
    renderPrice: function (product, variant = null, options = {}) {
        let priceClasses = options.priceClasses || [];

        let priceBadge = '';
        const priceBadgeTag = product.getTags().find(tag => tag.indexOf('price-badge ') === 0);
        if (priceBadgeTag) {
            priceClasses.push('price--with-badge');
            priceBadge = `
                <span class="badge badge--price">${priceBadgeTag.replace('price-badge ', '')}</span>
            `;
        }

        let priceVaries = false;
        if (!variant) {
            const price = product.firstVariant().getPrice();
            priceVaries = Boolean(product.getVariants().find(variant => variant.getPrice() !== price));
        }

        const selectedVariant = variant || product.firstAvailableVariant() || product.firstVariant();

        let finalPriceCents = selectedVariant.getPrice();

        if (options.discount) {
            finalPriceCents -= Math.round(finalPriceCents * (options.discount / 100));
        }

        let moneyPrice = BryanAnthonys.formatMoney(finalPriceCents);
        if (priceVaries) {
            moneyPrice = BryanAnthonys.strings.price.fromPrice.replace('{{ price }}', moneyPrice);
        }

        const compareAtPriceCents = selectedVariant.getCompareAtPrice() || (options.discount ? selectedVariant.getPrice() : null);
        let compareAtPrice = '';
        let discountPercentage = '';
        if (compareAtPriceCents) {
            priceClasses.push('price--on-sale');
            compareAtPrice = BryanAnthonys.formatMoney(compareAtPriceCents);
            discountPercentage = Math.round(
                ((compareAtPriceCents - finalPriceCents) * 100) / compareAtPriceCents
            );
            discountPercentage = BryanAnthonys.strings.price.discountPercentage.replace('{{ discount }}', discountPercentage);
        }

        return `
            <div class="price ${priceClasses.join(' ')}" data-price>
                <div class="price__container">
                    <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">
                            ${BryanAnthonys.strings.price.regular}
                        </span>
                        <span class="price-item price-item--regular">${moneyPrice}</span>
                    </div>
                    
                    <div class="price__sale">
                          <span class="visually-hidden visually-hidden--inline">${BryanAnthonys.strings.price.sale}</span>
                          <span class="price-item price-item--sale">${moneyPrice}</span>
                          <span class="visually-hidden visually-hidden--inline">
                            ${BryanAnthonys.strings.price.regular}
                          </span>
                        <span><s class="price-item price-item--regular">${compareAtPrice}</s></span>
                        <span class="price-item price-item--discount-percentage">${discountPercentage}</span>
                    </div>
                    
                    ${priceBadge}
                </div>
            </div>
        `;
    },
    /**
     * @param product {JsonProduct}
     * @param variant {JsonVariant}
     * @param options {object}
     * @returns {string}
     */
    renderBadges: function (product, variant = null, options = {}) {
        const limit = options.limit || 2;
        const badges = [];

        for (let badgeType of BryanAnthonys.cardBadgeTypes) {
            if (badges.length >= limit) {
                break;
            }

            switch (badgeType) {
                case 'nostock': {
                    if (!product.isAvailable() && !product.getTags().includes('PDP for bundle')) {
                        badges.push(`
                            <span class="badge product-badge product-badge--${badgeType} product-badge--top-left">
                                ${BryanAnthonys.strings.badges.outOfStock}
                            </span>
                        `);
                    }
                    break;
                }
                case 'lowstock': {
                    const selectedVariant = variant || product.firstAvailableVariant();
                    const quantity = selectedVariant ? selectedVariant.getInventoryQuantity() : null;
                    if (
                        quantity !== null
                        && quantity > 0
                        && quantity < BryanAnthonys.lowStockThreshold
                        && !BryanAnthonys.printTypes.includes(product.getType())
                    ) {
                        badges.push(`
                            <span class="badge product-badge product-badge--${badgeType} product-badge--top-left">
                                ${BryanAnthonys.strings.badges.lowStock}
                            </span>
                        `);
                    }
                    break;
                }
                case 'preorder': {
                    if (product.isOnPreorder() && !product.isSoftPreorder()) {
                        badges.push(`
                            <span class="badge product-badge product-badge--${badgeType} product-badge--top-left">
                                ${BryanAnthonys.strings.badges.preorder}
                            </span>
                        `);
                    }
                    break;
                }
                case 'custom': {
                    const badgeTag = product.getTags().find(tag => tag.includes('badge-'));
                    if (badgeTag) {
                        const badgeTagParts = badgeTag.split('badge-');
                        badges.push(`
                            <span class="badge product-badge product-badge--${badgeType} product-badge--${badgeTagParts[1]}">
                                ${badgeTagParts[0].toUpperCase()}
                            </span>
                        `);
                    }
                    break;
                }
                case 'new': {
                    const publishedAt = product.getDate('published_at');
                    if (publishedAt) {
                        const newProductThreshold = new Date();
                        newProductThreshold.setDate(newProductThreshold.getDate() - BryanAnthonys.newProductThreshold);
                        if (publishedAt.getTime() > newProductThreshold.getTime()) {
                            badges.push(`
                                <span class="badge product-badge product-badge--${badgeType} product-badge--top-left">
                                     ${BryanAnthonys.strings.badges.new}
                                </span>
                            `);
                        }
                    }
                    break;
                }
            }
        }

        return badges.join('');
    },
    /**
     * @param product {JsonProduct}
     * @returns {string}
     */
    renderRating: function (product) {
        const tags = product.getTags();

        const averageRatingTag = tags.find(tag => tag.indexOf('avg-rating-') === 0);
        if (!averageRatingTag) {
            return '';
        }

        const reviewsCountTag = tags.find(tag => tag.indexOf('reviews-count-') === 0);
        if (!reviewsCountTag) {
            return '';
        }

        const averageRating = parseFloat(averageRatingTag.replace('avg-rating-', ''));
        const reviewsCount = parseInt(reviewsCountTag.replace('reviews-count-', ''));
        const ratingsPercentage = Math.round((averageRating / 5) * 100);
        return `
            <div 
                class="product-card-rating mb-2 size-11 size-12-desktop light-weight" role="img" 
                aria-label="${BryanAnthonys.strings.accessibility.starReviewsInfo.replace('{{ ratings_average }}', averageRating.toFixed(2))}"
            >
                <span class="product-rating-stars product-card-rating__stars">
                  <span class="product-rating-full-stars product-card-rating__full-stars"
                    style="width: ${ratingsPercentage}%"></span>
                </span>
                <span aria-hidden="true" class="product-card-rating__count">
                    ${BryanAnthonys.strings.accessibility.totalReviews.replace('{{ ratings_count }}', reviewsCount)}
                </span>
            </div>
        `;
    },
    /**
     * @param product
     * @param selectedVariant
     * @param optionNames
     * @param configOptions
     * @returns {string}
     */
    renderSwatches: function (product, selectedVariant, optionNames, configOptions = {}) {
        if (product.getVariants().length < 2) {
            return '';
        }

        const optionsToShow = optionNames === 'all'
            ? product.getOptions().map(option => option.name)
            : optionNames.split(',');

        const imageOptions = BryanAnthonys.swatches.imageSwatchesOptions.split(',');
        const swatchGroups = [];
        const variants = product.getVariants();

        product.getOptions().forEach(option => {
            if (!optionsToShow.includes(option.name)) {
                return;
            }

            const buttons = [];
            let activeOption = '';
            option.values.forEach(value => {
                const availableVariant = variants.find(variant => {
                    return variant.getOption(option.position) === value && variant.isAvailable();
                });
                const swatchClasses = ['swatch-button', 'mr-1'];
                let content = `<span aria-hidden="true">${value}</span>`;
                if (imageOptions.includes(option.name)) {
                    swatchClasses.push('swatch-button--image');
                    const image = BryanAnthonys.swatches.swatchImagePattern.replace('[handle]', BryanAnthonys.handleize(value));
                    content = `
                        <span aria-hidden="true" class="swatch-button__image" style="background-image: url(${image})">
                            &nbsp;
                        </span>
                    `;
                }

                if (selectedVariant.getOption(option.position) === value) {
                    swatchClasses.push('swatch-button--active');
                    if (configOptions.showActiveOption && imageOptions.includes(option.name)) {
                        activeOption = selectedVariant.getOption(option.position);
                    }
                }

                if (!availableVariant) {
                    swatchClasses.push('swatch-button--out-of-stock');
                }

                buttons.push(`
                    <button
                        type="button" class="${swatchClasses.join(' ')}" tabindex="${configOptions.tabFocusable ? 0 : -1}"
                        data-value="${BryanAnthonys.escapeHtml(value)}" data-position="${option.position}"
                        aria-label="${BryanAnthonys.escapeHtml(option.name)} ${BryanAnthonys.escapeHtml(value)}"
                        title="${BryanAnthonys.escapeHtml(option.name)} ${BryanAnthonys.escapeHtml(value)}"
                      >${content}</button>
                `);
            });

            swatchGroups.push(`
                <swatch-group class="swatch-group block" data-option="${BryanAnthonys.escapeHtml(option.name)}">
                    ${configOptions.showOptionName ? `<span class="swatch-group__name size-13 heavy-weight mr-1">${option.name}:</span>` : ''}
                    ${buttons.join('')}
                    ${activeOption ? `<span data-active-option class="swatch-group__active-option text-subdued size-12">${activeOption}</span>` : ''}
                </swatch-group>
            `);

        });

        return `
            <swatch-buttons 
                class="block mt-2" data-variant-id="${selectedVariant.getId()}" 
                data-product-id="${product.getId()}" data-product-handle="${product.getHandle()}"
            >
                ${swatchGroups.join('')}
            </swatch-buttons>
        `;
    },
    /**
     * @param product
     * @param selectedVariant
     * @param options
     * @returns {string}
     */
    renderGallery: function (product, selectedVariant, options = {}) {
        if (!product.hasImages()) {
            return '';
        }

        const variantImages = {};
        product.getVariants().forEach(variant => {
            const variantImage = variant.getImage();
            if (variantImage) {
                const src = variantImage.src.includes('https://') ? variantImage.src : `https:${variantImage.src}`;
                variantImages[src] = variantImages[src] || [];
                variantImages[src].push(variant.getId());
            }
        });

        let images = product.getImages();
        const variantImage = selectedVariant.getImage(images);
        if (JSON.stringify(variantImages) !== '{}' && variantImage) {
            images = images.filter(image => {
                let imageSrc = image.src.includes('https://') ? image.src : `https:${image.src}`;

                return !variantImages[imageSrc];
            });

            images.unshift(variantImage);
        }
        const numericId = Math.round(Math.random() * 1000000);
        const sliderId = `Slider-Js-Gallery-${numericId}`;
        const padding = Math.floor(images[0].getHeightRatio() * 100);
        const slides = images.map((image, index) => `
            <li id="Slide-Js-Gallery-${numericId}-${index}" class="grid__item slider__slide slider__slide--stop">
                <div class="media product-media" style="padding-bottom: ${padding}%;">
                    <img 
                        src="${BryanAnthonys.resizeImage(image.src, 1200)}" width="${image.width}" height="${image.height}"
                        alt=${JSON.stringify(image.alt || product.getTitle())}  loading="lazy"
                    />
                </div>
            </li>
        `);

        return `
            <slider-component class="slider-component-desktop ${options.cssClasses || ''}">
                <ul 
                    id="${sliderId}" role="list" aria-label="Slider" 
                    class="grid slider slider--tablet slider--desktop ${options.listCssClasses || 'mt-0 mb-3 grid--1-partial-col'}"
                >
                    ${slides.join('')}
                </ul>
                <div class="slider-buttons no-js-hidden slider-buttons--sides">
                    <button type="button" class="slider-button slider-button--prev slider-button--highlight" name="previous"
                            aria-label="Slide left" aria-controls="${sliderId}" disabled="disabled">
                        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 46 26" fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                            <path d="M45 1L22.9127 25L1 0.999998" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round"></path>
                        </svg>
                
                    </button>
                    <div class="slider-counter caption visually-hidden">
                        <span class="slider-counter--current">1</span>
                        <span aria-hidden="true"> / </span>
                        <span class="visually-hidden">of</span>
                        <span class="slider-counter--total">${images.length}</span>
                    </div>
                    <button type="button" class="slider-button slider-button--next slider-button--highlight" name="next"
                            aria-label="Slide right" aria-controls="${sliderId}" disabled="disabled">
                        <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 46 26" fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                            <path d="M45 1L22.9127 25L1 0.999998" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                  stroke-linejoin="round"></path>
                        </svg>
                
                    </button>
                </div>
                <global-element-initializer></global-element-initializer>
            </slider-component>
        `;
    },
    /**
     * Warning: Products that are not found will be skipped without warning
     * @param handles
     * @param apiVersion
     * @returns {Promise<unknown>}
     */
    fetchFullProductsByHandles: function (handles, apiVersion = null) {
        const productQueries = [];
        const productHandles = {};
        const variableTypes = [];

        handles.forEach((handle, index) => {
            productHandles[`productHandle${index}`] = handle;
            variableTypes.push(`$productHandle${index}: String!`);
            productQueries.push(`
                product_${index}: productByHandle(handle: $productHandle${index}) {
                    id
                    title
                    tags
                    handle
                    createdAt
                    updatedAt
                    publishedAt
                    descriptionHtml
                    availableForSale
                    isGiftCard
                    productType
                    requiresSellingPlan
                    options(first: 100) {
                       name
                       values
                    }
                    variants(first: 100) {
                        edges {
                            node {
                                id
                                title,
                                availableForSale
                                currentlyNotInStock
                                compareAtPrice {
                                  amount
                                  currencyCode
                                }
                                currentlyNotInStock
                                price {
                                  amount
                                  currencyCode
                                }
                                selectedOptions {
                                    name
                                    value
                                }
                                image {
                                    id
                                }
                            }
                        }
                    }
                    images(first: 100) {
                        edges {
                            node {
                                altText
                                height
                                width
                                id
                                url
                            }
                        }
                    }
                }
            `);
        });

        const query = `query getFullProductsByHandles(${variableTypes.join(', ')}) {
            ${productQueries.join('')}
        }`;

        return new Promise(function (resolve, reject) {
            StorefrontCatalog.executeGraphQuery(query, productHandles, apiVersion)
                .then(BryanAnthonys.decodeJsonResponse)
                .then(response => {
                    if (response.errors && response.errors.length) {
                        throw response.errors;
                    }

                    const products = [];
                    for (const position in response.data) {
                        if (response.data[position]) {
                            products.push(response.data[position]);
                        }
                    }
                    resolve(products);
                })
                .catch(error => reject(error));
        });
    },
    executeGraphQuery: function (query, variables = {}, apiVersion = null) {
        if (!apiVersion) {
            apiVersion = BryanAnthonys.storefrontApiVersion;
        }

        const apiUrl = `https://${BryanAnthonys.permanentDomain}/api/${apiVersion}/graphql.json`;
        const headers = new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Shopify-Storefront-Access-Token': BryanAnthonys.storefrontApiKey
        });

        return fetch(apiUrl, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({query, variables})
        });
    },
    idFromGid: function (gid) {
        return parseInt(gid.split('/').pop());
    },
};